import { useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { QA_NAME_CLEAR_BTN } from '../../constants/qa'
import Button, { ButtonArrow } from '../Button'
import { Tag, TAG_APPEARANCE, TAG_HEIGHT } from '../Tag'

import { TAG_BAR_MAX_OFFSET_HEIGHT } from './TagBar.constants'

import * as styles from './TagBar.module.css'

function TagBar({
  TagComponent = Tag,
  className = styles.TagBar,
  disabled,
  getColor,
  getIconColor,
  getText,
  getTextColor,
  getTooltip,
  items,
  mapTagProps,
  maxBarOffsetHeight = TAG_BAR_MAX_OFFSET_HEIGHT,
  size = TAG_HEIGHT.height24,
  onChange,
  onClear,
}) {
  const [showAllItems, setShowAllItems] = useState(false)
  const [overflowing, setOverflowing] = useState(false)

  const barRef = useRef(null)

  const { t } = useTranslation()

  useLayoutEffect(() => {
    if (barRef.current) {
      function updateOverflow() {
        const scrollHeight = barRef.current.scrollHeight

        const nextOverflowing = scrollHeight > maxBarOffsetHeight

        setOverflowing(nextOverflowing)

        if (!nextOverflowing) {
          setShowAllItems(false)
        }
      }

      updateOverflow()

      const resizeObserver = new ResizeObserver(updateOverflow)

      resizeObserver.observe(barRef.current)

      return () => resizeObserver.disconnect()
    }
  }, [items, showAllItems])

  function handleToggleShowAllItems() {
    setShowAllItems(prevShowAll => !prevShowAll)
  }

  function getHandleChange(item) {
    return () => {
      if (item.onClick) {
        item.onClick()
      } else {
        onChange(item)
      }
    }
  }

  const wrapperClass = classNames(styles.tagWrapper, {
    [styles.hidden]: !showAllItems && overflowing,
  })

  return items.length > 0 ? (
    <div className={className}>
      <div className={wrapperClass} ref={barRef}>
        <Button.Contained
          isLight
          disabled={disabled}
          height={size}
          qaName={QA_NAME_CLEAR_BTN}
          text={t('Select.ClearAll')}
          onClick={onClear}
        />

        {items.map((item, index) => {
          const tagProps = {
            appearance: TAG_APPEARANCE.filled,
            backgroundColor: getColor?.(item),
            className: styles.tag,
            height: size,
            isDisabled: disabled,
            removeIconColor: getIconColor?.(item),
            text: getText(item),
            textColor: getTextColor?.(item),
            tooltip: getTooltip?.(item),
            onRemove: getHandleChange(item),
          }

          return (
            <TagComponent
              {...(mapTagProps ? mapTagProps(tagProps, item) : tagProps)}
              key={item.value || item.id || index}
            />
          )
        })}
      </div>

      {overflowing ? (
        <ButtonArrow
          Button={Button.Contained}
          className={styles.showButton}
          closeTooltipText='Select.ShowLess'
          disabled={disabled}
          height={size}
          isOpen={showAllItems}
          openTooltipText={{
            key: 'Select.ShowAll',
            values: { count: items.length },
          }}
          text={
            showAllItems
              ? t('Select.ShowLess')
              : t('Select.ShowAll', { count: items.length })
          }
          onClick={handleToggleShowAllItems}
        />
      ) : null}
    </div>
  ) : null
}

export default TagBar
